import {
  Box,
  styled,
  Slide,
  useMediaQuery,
  Fade,
  useTheme,
  type FadeProps,
  type SlideProps,
  Stack,
  Typography,
} from '@mui/material';
import {
  getSupportEmail,
  type CharityVettingFormValues,
  CountryCodeEnum,
} from 'features/charity-vetting/types/charityVettingTypes';
import { forwardRef, type KeyboardEvent, type PropsWithChildren } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import ActionDialog from 'ui/ActionDialog/ActionDialog';
import { CloseIcon } from 'ui/SvgIcon';

import { StyledLink } from '../StyledComponents';

import msg from './messages';

const StyledActionDialog = styled(ActionDialog)(({ theme: { breakpoints } }) => ({
  [breakpoints.down('md')]: {
    '& .MuiPaper-root': {
      height: '50vh',
      position: 'fixed',
      bottom: 0,
      margin: 0,
      width: '100vw',
      maxWidth: '100vw',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  '& .MuiDialogContent-root': {
    margin: 0,
  },
}));

const StyledHeader = styled(Box)(({ theme: { spacing, breakpoints } }) => ({
  display: 'flex',
  padding: spacing(2, 2, 0),
  justifyContent: 'space-between',
  width: '100%',
  [breakpoints.up('md')]: {
    padding: spacing(2),
  },
}));

const StyledTitle = styled(Typography)(({ theme: { spacing, palette, breakpoints } }) => ({
  fontWeight: 700,
  color: palette.cvi.neutrals[80],
  [breakpoints.up('md')]: {
    padding: spacing(2, 0, 0, 1),
  },
}));

const StyledContent = styled(Stack)(({ theme: { spacing, palette, breakpoints } }) => ({
  padding: spacing(1, 2, 2),
  gap: spacing(5),
  color: palette.cvi.neutrals[80],
  [breakpoints.up('md')]: {
    padding: spacing(0, 3, 4),
    gap: spacing(4),
  },
}));

interface ContactUsDialogProps {
  openDialog: boolean;
  toggleDialog: () => void;
}

const SlideTransition = forwardRef<HTMLDivElement, PropsWithChildren<SlideProps>>((props, ref) => {
  return <Slide direction='up' ref={ref} {...props} timeout={500} />;
});

const FadeTransition = forwardRef<HTMLDivElement, PropsWithChildren<FadeProps>>((props, ref) => {
  return <Fade ref={ref} {...props} />;
});

const ContactUsDialog = ({ openDialog, toggleDialog }: ContactUsDialogProps) => {
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('md'));
  const { control } = useFormContext<CharityVettingFormValues>();
  const countryIsoCode = useWatch({ control, name: 'charityOnboarding.countryIsoCode' });

  const SUPPORT_EMAIL = getSupportEmail(countryIsoCode ?? CountryCodeEnum.GB);

  const handleKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDialog();
    }
  };

  return (
    <StyledActionDialog
      onClose={toggleDialog}
      open={openDialog}
      size='sm'
      hideDefaultDialogActions
      TransitionComponent={isMobile ? SlideTransition : FadeTransition}
    >
      <StyledHeader>
        <StyledTitle variant='h2'>
          <FormattedMessage {...msg.charityVettingContactDialogLabel} />
        </StyledTitle>
        <div>
          <CloseIcon
            tabIndex={0}
            onClick={toggleDialog}
            onKeyDown={handleKeyDown}
            sx={{ cursor: 'pointer' }}
          />
        </div>
      </StyledHeader>
      <StyledContent>
        <Typography>
          <FormattedMessage {...msg.charityVettingContactDialogText} />
        </Typography>
        <StyledLink
          tabIndex={0}
          sx={{ mt: 0 }}
          href={`mailto:${SUPPORT_EMAIL}`}
          rel='noopener noreferrer'
        >
          {SUPPORT_EMAIL}
        </StyledLink>
      </StyledContent>
    </StyledActionDialog>
  );
};

export default ContactUsDialog;
